import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Header } from '../components/Header';
import {
  Download,
  CreditCard,
  CheckCircle2,
  Clock,
  Calendar,
  User,
  Sparkles,
  AlertTriangle,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusBadge = (status) => {
  const map = {
    paid: { bg: 'bg-[#06D6A0]/10', text: 'text-[#06D6A0]', label: 'Paid in full' },
    partial: { bg: 'bg-[#FFD166]/20', text: 'text-[#B8860B]', label: 'Deposit received' },
    unpaid: { bg: 'bg-[#FF3B6B]/10', text: 'text-[#FF3B6B]', label: 'Awaiting payment' },
  };
  return map[status] || map.unpaid;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-CA', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const formatTimeSlot = (slot) => {
  if (!slot) return '';
  const map = { '10:00': '10:00 AM', '13:00': '1:00 PM', '16:00': '4:00 PM' };
  return map[slot] || slot;
};

export const InvoiceView = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payEmail, setPayEmail] = useState('');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/invoice/${bookingId}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Invoice not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [bookingId]);

  // On return from Stripe: ?paid=1&session_id=… — poll payment status once
  useEffect(() => {
    if (searchParams.get('paid') === '1' && searchParams.get('session_id')) {
      const sid = searchParams.get('session_id');
      toast.success('Payment received — refreshing your invoice…');
      axios.get(`${API_URL}/api/payments/status/${sid}`).catch(() => {});
      // Celebrate the balance payment with a short confetti burst
      try {
        if (!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
          const BRAND = ['#FF3B6B', '#FF7A2E', '#FFD166', '#06D6A0', '#6366F1', '#FFB5D8'];
          confetti({ particleCount: 90, spread: 80, origin: { y: 0.5 }, colors: BRAND, scalar: 1.0, ticks: 220 });
          setTimeout(() => confetti({ particleCount: 40, angle: 60, spread: 55, startVelocity: 50, origin: { x: 0, y: 0.7 }, colors: BRAND }), 180);
          setTimeout(() => confetti({ particleCount: 40, angle: 120, spread: 55, startVelocity: 50, origin: { x: 1, y: 0.7 }, colors: BRAND }), 180);
        }
      } catch { /* ignore */ }
      // Reload invoice after short delay
      const t = setTimeout(async () => {
        try {
          const res = await axios.get(`${API_URL}/api/public/invoice/${bookingId}`);
          setData(res.data);
        } catch { /* ignore */ }
      }, 1200);
      return () => clearTimeout(t);
    }
    if (searchParams.get('cancelled') === '1') {
      toast('Payment cancelled — you can try again anytime.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, bookingId]);

  const startBalancePayment = async () => {
    if (!payEmail) { toast.error('Enter the email on your booking'); return; }
    setPaying(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/invoice/${bookingId}/pay-balance`, {
        email: payEmail.trim(),
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not start payment. Please try again.');
        setPaying(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start payment');
      setPaying(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/public/invoice/${bookingId}/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data?.invoice?.invoice_id || 'invoice'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (e) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-16">
          <div
            className="bg-white rounded-2xl border border-[#E8E5DF] p-8 text-center"
            data-testid="invoice-error"
          >
            <AlertTriangle className="w-12 h-12 text-[#FF3B6B] mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {error}
            </h2>
            <p className="text-[#6B6B7A] mb-6">
              Double-check the link from your email. If you need a fresh copy, reach out and we'll re-send it.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full"
              data-testid="invoice-back-home"
            >
              Back to site
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { invoice, booking } = data;
  const badge = statusBadge(invoice.status);
  const canPayBalance = !booking.balance_paid && invoice.balance_due > 0.01;
  const payLabel = booking.deposit_paid ? 'Pay Remaining Balance' : 'Pay Now';

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {/* LEFT: Invoice card */}
          <div className="md:col-span-2 bg-white rounded-3xl border border-[#E8E5DF] p-6 sm:p-8 shadow-sm" data-testid="invoice-card">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#FF3B6B]" />
                  <p className="text-sm tracking-wide text-[#6B6B7A] uppercase">Creative Birthdays</p>
                </div>
                <h1 className="text-3xl sm:text-4xl font-semibold text-[#1E1E24] mt-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Invoice
                </h1>
                <p className="text-sm text-[#6B6B7A] font-mono mt-1">{invoice.invoice_id}</p>
              </div>
              <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`} data-testid="invoice-status-badge">
                {badge.label}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-sm">
              <div>
                <p className="text-[#6B6B7A] mb-1">Bill to</p>
                <p className="font-medium text-[#1E1E24]">{invoice.customer_name}</p>
                <p className="text-[#6B6B7A] text-xs">{invoice.customer_email}</p>
              </div>
              <div>
                <p className="text-[#6B6B7A] mb-1">Party details</p>
                <p className="font-medium text-[#1E1E24] flex items-center gap-1">
                  <User className="w-4 h-4 text-[#FF3B6B]" /> {booking.child_name}
                </p>
                <p className="text-[#6B6B7A] text-xs flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" /> {formatDate(booking.date)} · {formatTimeSlot(booking.time_slot)}
                </p>
              </div>
            </div>

            <div className="border-t border-[#E8E5DF] pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#6B6B7A]">{invoice.package_name} Package</span>
                <span className="text-[#1E1E24]">${invoice.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B6B7A]">Tax</span>
                <span className="text-[#1E1E24]">${invoice.tax?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold pt-2 border-t border-[#E8E5DF] text-[#1E1E24]">
                <span>Total</span>
                <span data-testid="invoice-total">${invoice.total?.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-5 rounded-xl bg-[#FFFDF8] border border-[#E8E5DF] p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#06D6A0] font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> Amount paid
                </span>
                <span className="text-[#06D6A0] font-semibold" data-testid="invoice-amount-paid">
                  ${invoice.amount_paid?.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#FF3B6B] font-medium flex items-center gap-1">
                  <Clock className="w-4 h-4" /> Balance due
                </span>
                <span className="text-[#FF3B6B] font-semibold text-lg" data-testid="invoice-balance-due">
                  ${invoice.balance_due?.toFixed(2)}
                </span>
              </div>
              {invoice.due_date && (
                <p className="text-xs text-[#6B6B7A]">Due by {invoice.due_date}</p>
              )}
            </div>

            {invoice.status === 'paid' && (
              <div className="mt-5 rounded-xl bg-[#06D6A0]/10 border border-[#06D6A0]/30 p-4 text-sm text-[#07A783]" data-testid="invoice-paid-banner">
                <p className="font-semibold">This invoice is fully paid. Thank you!</p>
                <p className="text-xs opacity-80">We can't wait to celebrate {booking.child_name}'s big day.</p>
              </div>
            )}
          </div>

          {/* RIGHT: Actions */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-3xl border border-[#E8E5DF] p-6 sticky top-24 shadow-sm space-y-3">
              <h3 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Next steps
              </h3>

              {canPayBalance && (
                <Button
                  onClick={() => { setPayEmail(invoice.customer_email || ''); setPayOpen(true); }}
                  className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-6"
                  data-testid="invoice-pay-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {payLabel} · ${invoice.balance_due?.toFixed(2)}
                </Button>
              )}

              <Button
                onClick={handleDownloadPdf}
                variant="outline"
                disabled={downloading}
                className="w-full rounded-full border-[#E8E5DF] hover:bg-[#FFFDF8]"
                data-testid="invoice-download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading ? 'Preparing…' : 'Download PDF'}
              </Button>

              <div className="pt-3 border-t border-[#E8E5DF] text-xs text-[#6B6B7A] leading-relaxed">
                <p className="font-semibold text-[#1E1E24] mb-1">Questions about your booking?</p>
                <p>Reply to your confirmation email and our team will help out within one business day.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Pay balance dialog — email verification + Stripe redirect */}
      <Dialog open={payOpen} onOpenChange={(o) => { if (!paying) setPayOpen(o); }}>
        <DialogContent className="bg-white max-w-md" data-testid="pay-balance-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }} className="flex items-center gap-2 text-xl">
              <CreditCard className="w-5 h-5 text-[#FF3B6B]" /> Pay balance via Stripe
            </DialogTitle>
            <DialogDescription>
              We'll send you to Stripe's secure checkout to pay <strong>${data?.invoice?.balance_due?.toFixed(2)}</strong>. Confirm the email on your booking first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-semibold">Booking email</Label>
              <div className="relative mt-2">
                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B7A]" />
                <Input
                  type="email"
                  value={payEmail}
                  onChange={(e) => setPayEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-11 rounded-xl border-[#E8E5DF]"
                  data-testid="pay-balance-email"
                />
              </div>
              <p className="text-xs text-[#6B6B7A] mt-1">Must match the email on the booking for security.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPayOpen(false)} className="flex-1 rounded-full" disabled={paying}>Cancel</Button>
              <Button
                onClick={startBalancePayment}
                disabled={paying}
                className="flex-1 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
                data-testid="pay-balance-confirm"
              >
                {paying ? 'Redirecting…' : 'Continue to Stripe'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceView;
